"use client";

import { useMemo } from "react";
import { usePathname } from "next/navigation";
import { stegaClean } from "@sanity/client/stega";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";
import AutoScroll from "embla-carousel-auto-scroll";
import { cn } from "@/lib/utils";
import { NON_DEFAULT_LANGUAGES, DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";

const FALLBACK_WORDS = ["Krypto", "Investments", "Strategien", "Risikomanagement", "Rendite"];

function BannerMark({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 15.5542 14.8583"
      fill="currentColor"
      className={className}>
      <path d="M15.5541 11.1383C13.3838 11.1383 11.6176 9.38056 11.6176 7.22076H8.08865C8.08865 11.3174 11.4376 14.6501 15.5541 14.6501V11.1383Z" />
      <path d="M7.465 0C3.34851 0 0 3.33233 0 7.42892C0 11.5255 3.34851 14.8583 7.465 14.8583V11.3464C5.29471 11.3464 3.52893 9.58916 3.52893 7.42892C3.52893 5.26868 5.29471 3.51187 7.465 3.51187C9.63529 3.51187 11.4015 5.26912 11.4015 7.42892H14.9304C14.9304 3.33233 11.5815 0 7.465 0Z" />
    </svg>
  );
}

interface ScrollingBannerProps {
  words?: string[];
  wordsI18n?: { de?: string[]; br?: string[] };
  language?: string;
  className?: string;
  theme?: "light" | "dark";
  /**
   * "serif" is the text-only 2026 strip: Blacker Display uppercase at 20px,
   * separated by the IQ logo mark instead of a dot, on a 50px grey band.
   */
  variant?: "default" | "serif";
  wordClassName?: string;
  /**
   * Distance between words in rem, replacing the built-in 5rem. Set when a
   * page's strip is drawn with a different rhythm (the 2026 affiliate pages
   * use 6rem).
   */
  gapRem?: number;
  /** Overrides the separator between words (default: a 0.5rem round dot). */
  separatorClassName?: string;
  markClassName?: string;
}

export default function ScrollingBanner({
  words: wordsProp,
  wordsI18n,
  language: pageLanguage,
  className,
  theme = "light",
  variant = "default",
  wordClassName,
  gapRem,
  separatorClassName,
  markClassName,
}: ScrollingBannerProps) {
  const isSerif = variant === "serif";
  const pathname = usePathname();
  const language =
    stegaClean(pageLanguage) ||
    (NON_DEFAULT_LANGUAGES.find((l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`) ?? DEFAULT_LANGUAGE);

  const words = useMemo(() => {
    const base = wordsProp?.length ? wordsProp : FALLBACK_WORDS;
    if (!language || language === DEFAULT_LANGUAGE) return base;
    const translated = wordsI18n?.[language as keyof typeof wordsI18n];
    return translated?.length ? translated : base;
  }, [wordsProp, wordsI18n, language]);

  const autoScroll = useMemo(
    () =>
      AutoScroll({
        speed: 1.5,
        startDelay: 0,
        stopOnInteraction: false,
      }),
    [],
  );

  return (
    <div
      className={cn(
        "z-10 w-full overflow-hidden  border-y-[.125rem] h-[6.835rem] flex flex-col justify-center",
        theme === "dark" ? "border-white/10" : "border-black/10",
        isSerif && "h-[6.25rem] bg-[#F5F5F5] border-t-0 border-b-[0.165rem]",
        className,
      )}>
      <Carousel
        opts={{ loop: true }}
        plugins={[autoScroll]}
        className="w-full">
        <CarouselContent
          className={gapRem ? undefined : "-ml-[5rem]"}
          style={gapRem ? { marginLeft: `-${gapRem}rem` } : undefined}>
          {[...words, ...words, ...words].map((word, index) => (
            <CarouselItem
              key={index}
              className={cn("basis-auto", !gapRem && (isSerif ? "pl-[3.963rem]" : "pl-[5rem]"))}
              style={gapRem ? { paddingLeft: `${gapRem}rem` } : undefined}>
              <div
                className={cn("flex items-center", !gapRem && (isSerif ? "gap-[3.963rem]" : "gap-[5rem]"))}
                style={gapRem ? { gap: `${gapRem}rem` } : undefined}>
                <span
                  className={cn(
                    "block uppercase",
                    isSerif
                      ? "font-blacker lg:text-[2.5rem] text-[2rem] font-normal leading-[1.146] tracking-[-0.05rem]"
                      : "font-geist-mono lg:text-[2rem] text-[1.6rem] font-[500] tracking-[0.04rem]",
                    theme === "dark" ? "text-white/60" : "text-black/60",
                    wordClassName,
                  )}>
                  {word}
                </span>
                {isSerif ? (
                  <BannerMark
                    className={cn(
                      "w-[1.944rem] h-[1.857rem] shrink-0 text-black",
                      markClassName,
                    )}
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className={cn(
                      "h-[0.5rem] w-[0.5rem] shrink-0 rounded-full",
                      theme === "dark" ? "bg-white" : "bg-[#228E71]",
                      separatorClassName,
                    )}
                  />
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}
